import { CssVarsThemeOptions } from '@mui/joy';
import JoyGlobalStyles from '@mui/joy/GlobalStyles';
import type { PaletteRange } from '@mui/joy/styles';
import {
  CssVarsProvider as JoyCssVarsProvider,
  extendTheme,
} from '@mui/joy/styles';

import { overrides as JoyButtonOverrides } from '~/components/ui/button';
import { overrides as JoyCardOverrides } from '~/components/ui/card';
import { overrides as JoyCheckboxOverrides } from '~/components/ui/checkbox';
import { overrides as JoyInputOverrides } from '~/components/ui/input';
import { overrides as JoyLinkOverrides } from '~/components/ui/link';
import { overrides as JoySelectOverrides } from '~/components/ui/select';
import { overrides as JoySwitchOverrides } from '~/components/ui/switch';
import { overrides as JoyTabsOverrides } from '~/components/ui/tabs';

// You can put this to any file that's included in your tsconfig
declare module '@mui/joy/styles' {
  interface ColorPalettePropOverrides {
    // apply to all Joy UI components that support `color` prop
    secondary: true;
  }
  interface Palette {
    // this will make the node `secondary` configurable in `extendTheme`
    // and add `secondary` to the theme's palette.
    secondary: PaletteRange;

    gradient: {
      primary: string;
      secondary: string;
    };
    white: PaletteRange;
  }
  interface PalettePrimaryOverrides {
    950: true;
  }
  interface PaletteNeutralOverrides {
    950: true;
  }
  interface PaletteDangerOverrides {
    950: true;
  }
  interface PaletteSuccessOverrides {
    950: true;
  }
  interface PaletteWarningOverrides {
    950: true;
  }
}

const colorSchemes: CssVarsThemeOptions['colorSchemes'] = {
  light: {
    palette: {
      primary: {
        '50': '#EEFFFD',
        '100': '#C6FFFB',
        '200': '#8EFFF8',
        '300': '#4DFBF4',
        '400': '#19E8E6',
        '500': '#00CECF',
        '600': '#009FA4',
        '700': '#027E83',
        '800': '#086267',
        '900': '#0C5255',
        '950': '#002F34',

        solidActiveColor: 'var(--joy-palette-primary-500)',
        plainHoverBg: 'rgba(var(--joy-palette-primary-mainChannel) / 0.1)',
        softBg: 'rgba(var(--joy-palette-primary-mainChannel) / 0.1)',
        softHoverBg: 'var(--joy-palette-primary-600)',
        softHoverColor: 'var(--joy-palette-primary-50)',
        softActiveColor: 'var(--joy-palette-primary-600)',
        softActiveBg: 'var(--joy-palette-primary-100)',
      },
      secondary: {
        50: '#DBEDFF',
        100: '#BFDCFF',
        200: '#98C3FF',
        300: '#709FFF',
        400: '#ECF5FF',
        500: '#4E7AFF',
        600: '#2449FC',
        700: '#2340DF',
        800: '#1F38B4',
        900: '#22368D',
        //   950: '#141E52',

        // Adjust the global variant tokens as you'd like.
        // The tokens should be the same for all color schemes.
        solidBg: 'var(--joy-palette-secondary-600)',
        solidColor: 'var(--joy-palette-common-white)',
        solidHoverBg: 'var(--joy-palette-secondary-800)',
        solidActiveBg: 'var(--joy-palette-secondary-500)',
        solidDisabledBg: 'var(--joy-palette-primary-solidDisabledBg)',
        outlinedBorder: 'var(--joy-palette-secondary-500)',
        outlinedColor: 'var(--joy-palette-secondary-700)',
        outlinedHoverBg: 'var(--joy-palette-secondary-700)',
        outlinedHoverBorder: 'var(--joy-palette-secondary-700)',
        outlinedActiveBg: 'var(--joy-palette-secondary-100)',
        softColor: 'var(--joy-palette-secondary-800)',
        softBg: 'var(--joy-palette-secondary-50)',
        softActiveBg: 'var(--joy-palette-secondary-300)',
        plainColor: 'var(--joy-palette-secondary-700)',
        plainHoverBg: 'var(--joy-palette-secondary-50)',
        plainActiveBg: 'var(--joy-palette-secondary-100)',
      },
      success: {
        '50': '#D5FFE9',
        '100': '#AEFFD3',
        '200': '#70FFB2',
        '300': '#2BFD8A',
        '400': '#EDFFF5',
        '500': '#03FF75',
        '600': '#00C052',
        '700': '#009644',
        '800': '#067539',
        '900': '#076032',
        '950': '#00371A',

        solidBg: 'var(--joy-palette-success-700)',
        solidHoverBg: 'var(--joy-palette-success-800)',
        solidActiveBg: 'var(--joy-palette-success-900)',
        solidDisabledBg: 'var(--joy-palette-primary-solidDisabledBg)',
        softBg: 'var(--joy-palette-success-50)',
        softColor: 'var(--joy-palette-success-700)',
        plainColor: 'var(--joy-palette-success-700)',
        plainHoverBg: 'rgba(var(--joy-palette-success-mainChannel) / 0.1)',
        plainActiveBg: 'var(--joy-palette-success-100)',
      },
      warning: {
        '50': '#FEF3C7',
        '100': '#FDE58A',
        '200': '#FBD24E',
        '300': '#FABE25',
        '400': '#FFFBEB',
        '500': '#F49D0C',
        '600': '#D87607',
        '700': '#BC560A',
        '800': '#923F0E',
        '900': '#78340F',
        '950': '#451A03',

        solidDisabledBg: 'var(--joy-palette-primary-solidDisabledBg)',
        softBg: 'var(--joy-palette-warning-50)',
      },
      danger: {
        '50': '#FFE1E1',
        '100': '#FFC7C7',
        '200': '#FFA0A0',
        '300': '#FF7070',
        '400': '#FFF1F1',
        '500': '#F83B3B',
        '600': '#E51D1D',
        '700': '#C11414',
        '800': '#A01414',
        '900': '#841818',
        '950': '#480707',

        solidDisabledBg: 'var(--joy-palette-primary-solidDisabledBg)',
        plainHoverBg: 'var(--joy-palette-danger-50)',
        softBg: 'var(--joy-palette-danger-50)',
        softColor: 'var(--joy-palette-danger-700)',
        outlinedHoverBg: 'var(--joy-palette-danger-500)',
        outlinedHoverColor: 'var(--joy-palette-common-white)',
      },
      neutral: {
        '50': '#f6f6f6',
        '100': '#e7e7e7',
        '200': '#d1d1d1',
        '300': '#b0b0b0',
        '400': '#878787',
        '500': '#6D6D6D',
        '600': '#5D5D5D',
        '700': '#4F4F4F',
        '800': '#454545',
        '900': '#3D3D3D',
        '950': '#262626',

        solidDisabledBg: 'var(--joy-palette-primary-solidDisabledBg)',
        outlinedColor: 'var(--joy-palette-neutral-500)',
        outlinedBorder: 'var(--joy-palette-neutral-100)',
        outlinedHoverBg: 'var(--joy-palette-neutral-50)',
        plainHoverBg: 'var(--joy-palette-neutral-50)',
        plainActiveBg: 'var(--joy-palette-neutral-100)',
        softColor: 'var(--joy-palette-neutral-500)',
        softBg: 'var(--joy-palette-neutral-50)',
        softHoverBg: 'var(--joy-palette-neutral-100)',
      },
      common: { black: '#181818' },
      background: {
        surface: 'var(--joy-palette-common-white)',
        body: 'var(--joy-palette-neutral-50)',
        level1: 'var(--joy-palette-neutral-100)',
        level2: 'var(--joy-palette-neutral-100)',
        level3: 'var(--joy-palette-neutral-100)',
      },
      white: {
        solidBg: 'var(--joy-palette-common-white)',
        solidHoverBg: 'var(--joy-palette-neutral-50)',
        solidColor: 'var(--joy-palette-neutral-500)',
        solidActiveBg: 'var(--joy-palette-success-900)',
        softBg: 'var(--joy-palette-success-50)',
        softColor: 'var(--joy-palette-success-700)',
        plainHoverBg: 'var(--joy-palette-neutral-100)',
        plainColor: 'var(--joy-palette-common-white)',
        plainDisabledColor: 'var(--joy-palette-neutral-200)',
      },
      gradient: {
        primary:
          'linear-gradient(270deg,  var(--joy-palette-primary-500) 0%,var(--joy-palette-secondary-600) 100%)',
        secondary:
          'linear-gradient(293.51deg, var(--joy-palette-primary-500) 16.77%, var(--joy-palette-success-500) 53.83%)',
      },
    },
  },
  dark: {
    palette: {
      primary: {
        '50': '#EEFFFD',
        '100': '#C6FFFB',
        '200': '#8EFFF8',
        '300': '#4DFBF4',
        '400': '#19E8E6',
        '500': '#00CECF',
        '600': '#009FA4',
        '700': '#027E83',
        '800': '#086267',
        '900': '#0C5255',
        '950': '#002F34',

        solidActiveColor: 'var(--joy-palette-primary-500)',
        softBg: 'rgba(var(--joy-palette-primary-mainChannel) / 0.1)',
        plainColor: 'var(--joy-palette-neutral-300)',
        plainHoverBg: 'rgba(var(--joy-palette-primary-mainChannel) / 0.3)',
        plainHoverColor: 'var(--joy-palette-primary-500)',
      },
      secondary: {
        50: '#fdf2f8',
        100: '#fce7f3',
        200: '#fbcfe8',
        300: '#f9a8d4',
        400: '#f472b6',
        500: '#ec4899',
        600: '#db2777',
        700: '#be185d',
        800: '#9d174d',
        900: '#831843',

        // Adjust the global variant tokens as you'd like.
        // The tokens should be the same for all color schemes.
        solidBg: 'var(--joy-palette-primary-400)',
        solidActiveBg: 'var(--joy-palette-primary-500)',
        outlinedBorder: 'var(--joy-palette-primary-700)',
        outlinedColor: 'var(--joy-palette-primary-600)',
        outlinedActiveBg: 'var(--joy-palette-primary-900)',
        softColor: 'var(--joy-palette-primary-500)',
        softBg: 'var(--joy-palette-primary-900)',
        softActiveBg: 'var(--joy-palette-primary-800)',
        plainColor: 'var(--joy-palette-primary-500)',
        plainActiveBg: 'var(--joy-palette-primary-900)',
      },
      common: { black: '#181818' },
    },
  },
};

export const joyTheme = extendTheme({
  // Align Joy breakpoints with Tailwind defaults so JS media queries,
  // Joy responsive `sx` props, and Tailwind classes share one handoff.
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  fontFamily: {
    display: 'Schibsted Grotesk',
    body: 'Schibsted Grotesk',
  },
  components: {
    JoyInput: JoyInputOverrides,
    JoySelect: JoySelectOverrides,
    JoySwitch: JoySwitchOverrides,
    JoyCheckbox: JoyCheckboxOverrides,
    ...JoyButtonOverrides,
    ...JoyCardOverrides,
    ...JoyLinkOverrides,
    ...JoyTabsOverrides,
  },
  colorSchemes,
});

export function ThemeProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return (
    <JoyCssVarsProvider theme={joyTheme}>
      <JoyGlobalStyles
        styles={{
          svg: {
            margin: 'var(--Icon-margin)',
            fontSize: 'var(--Icon-fontSize, var(--joy-fontSize-xl2))',
            color:
              'var(--Icon-color, var(--joy-palette-text-icon, var(--joy-palette-neutral-500, #636B74)))',
          },
        }}
      />
      {children}
    </JoyCssVarsProvider>
  );
}
